/**
 * Chat screen constants and pure helpers (split out of ChatScreenEnhanced).
 */
import type { ChatSession } from '../../types';
import { brand } from '../../design-system/theme/tokens';

// Design tokens used by the chat screen
export const COLORS = {
  primary: brand.primary,
  secondary: '#F59E0B',
  success: '#10B981',
  error: '#EF4444',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },
  border: '#E5E7EB',
};

export const WAITING_BUBBLE_ID = -9999;
export const STREAMING_BUBBLE_ID = -9998;
export const PENDING_TASKS_KEY_PREFIX = '@chat_pending_tasks_v1';
export const PENDING_USER_MESSAGE_ID_BASE = -1000000;
// Shared fallback; the server's max_size_mb wins (src/config/uploadLimits.ts).
export { DEFAULT_AUDIO_MAX_MB } from '../../config/uploadLimits';
export const UNLIMITED_LIMIT_THRESHOLD = 999_000_000;

export const isUnlimitedLimit = (value?: number | null) =>
  typeof value === 'number' && Number.isFinite(value) && value >= UNLIMITED_LIMIT_THRESHOLD;

export const stripFileExtension = (filename?: string | null) => {
  const raw = (filename || '').trim();
  if (!raw) return '';
  const basename = raw.replace(/\\/g, '/').split('/').pop() || raw;
  return basename.replace(/\.[^/.]+$/, '').trim();
};

export const getVisibleUserMessageContent = (content: string) => {
  // Older analysis messages persisted the full internal prompt after the
  // upload line. Keep that context in storage, but never expose it in chat.
  const uploadHeader = content.match(/^(?:📎\s*)?(?:File uploaded|Dosya yüklendi):[^\r\n]*/i);
  return uploadHeader?.[0].trim() || content;
};

export const sanitizeDownloadName = (name: string) =>
  name.replace(/[\x00-\x1f\x7f-\x9f<>:"/\\|?*]/g, '_').replace(/\s+/g, ' ').trim();

export const buildAnalysisPdfFilename = (
  session: ChatSession | undefined,
  language: string,
  sessionId: number
) => {
  const source = session?.source_document_filename || session?.title || `analysis_${sessionId}`;
  let base = stripFileExtension(source) || `analysis_${sessionId}`;
  const suffix = language === 'en' ? 'analysis' : 'analiz';
  const lowerBase = base.toLocaleLowerCase(language === 'tr' ? 'tr-TR' : 'en-US');
  if (!lowerBase.includes('analysis') && !lowerBase.includes('analiz')) {
    base = `${base} ${suffix}`;
  }
  return `${sanitizeDownloadName(base) || `analysis_${sessionId}`}.pdf`;
};

export type PendingTask = {
  taskId: string;
  sessionId: number;
  kind: 'chat' | 'analysis';
  createdAt: number;
  userMessage?: string;
  filename?: string;
  instructions?: string;
};
